"use client";

import { useSession, signOut } from "next-auth/react";
import { useEffect, useRef } from "react";
import Cookies from "js-cookie";
import useLocalizedRouter from "@/hooks/use-localized-router";
import { postRequest } from "@/utils/request";
import { stripLocalePrefix } from "@/utils/localize-path";

const SESSION_FLAG_KEY = "offerz-had-session";

async function performSessionExpiredLogout() {
  localStorage.removeItem(SESSION_FLAG_KEY);
  const lang = Cookies.get("locale") ?? "de";
  const currentPath = stripLocalePrefix(window.location.pathname);
  const callbackUrl = `/login?redirect=${encodeURIComponent(currentPath)}`;
  await postRequest({ path: { name: "logout" }, method: "POST", lang });
  signOut({ callbackUrl });
}

export default function SessionMonitor() {
  const { data: session, status } = useSession();
  const wasAuthenticatedRef = useRef(false);
  const sessionRef = useRef(session);
  const router = useLocalizedRouter();

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    const checkToken = setInterval(() => {
      const token = Cookies.get("app_token");
      if (!token) {
        router.replace("/");
      }
    }, 1000);
    return () => clearInterval(checkToken);
  }, []);

  useEffect(() => {
    if (status === "authenticated") {
      wasAuthenticatedRef.current = true;
      localStorage.setItem(SESSION_FLAG_KEY, "1");
    }
  }, [status]);

  // When NextAuth detects an expired JWT and flips to unauthenticated,
  // session becomes null and the interval-based check exits early.
  // This catches that transition immediately on any page, including fresh
  // page loads after dormancy where wasAuthenticatedRef would start as false
  // (handled via the localStorage flag set during the previous session).
  useEffect(() => {
    if (status !== "unauthenticated") return;
    if (wasAuthenticatedRef.current || localStorage.getItem(SESSION_FLAG_KEY)) {
      performSessionExpiredLogout();
    }
  }, [status]);

  useEffect(() => {
    const checkSession = setInterval(() => {
      const s = sessionRef.current;
      if (!s?.expires) return;

      const expiresTimestamp = new Date(s.expires).getTime();
      const currentTimestamp = Date.now();
      const timeRemaining = expiresTimestamp - currentTimestamp;

      if (timeRemaining < 0) {
        performSessionExpiredLogout();
      }
    }, 30000);

    return () => clearInterval(checkSession);
  }, []);

  return null;
}
